import { getStatusCssClass as _getStatusCssClass, getStatusLabel as _getStatusLabelFn } from '../../../shared/utils/statusConstants';

/**
 * proposalHelpers.js
 * Pure utility functions extracted from MyProposals.jsx (Phase 7 refactor).
 * Zero logic changes — identical implementations moved to a shared helpers file.
 */

export const formatErrorDetail = (detail) => {
    if (!detail) return 'An unexpected error occurred.';
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) {
        return detail.map(err => {
            if (typeof err === 'string') return err;
            if (typeof err === 'object') return `${err.msg || 'Error'} at ${err.loc?.join(' > ') || 'unknown'}`;
            return JSON.stringify(err);
        }).join(', ');
    }
    if (typeof detail === 'object') return JSON.stringify(detail);
    return String(detail);
};

// Robust date parser for varied formats (DD-MM-YYYY, th/st ordinals)
export const parseDateStr = (dateStr) => {
    if (!dateStr || dateStr === 'N/A' || dateStr === 'Not Specified') return null;
    let cleanStr = String(dateStr).trim();

    if (/\d{4}-\d{2}-\d{2}/.test(cleanStr)) {
        const d = new Date(cleanStr);
        return isNaN(d.getTime()) ? null : d;
    }

    const dmyMatch = cleanStr.match(/^(\d{2})[-/](\d{2})[-/](\d{4})/);
    if (dmyMatch) {
        cleanStr = `${dmyMatch[3]}-${dmyMatch[2]}-${dmyMatch[1]}`;
    } else {
        cleanStr = cleanStr.replace(/(\d+)(st|nd|rd|th)/g, '$1');
        const yearMatch = cleanStr.match(/\b\d{4}\b/);
        if (yearMatch) {
            const yearIndex = cleanStr.indexOf(yearMatch[0]);
            cleanStr = cleanStr.substring(0, yearIndex + 4);
        } else {
            cleanStr = cleanStr.split(',')[0].trim();
        }
    }
    const d = new Date(cleanStr);
    return isNaN(d.getTime()) ? null : d;
};

// Returns true if dateStr is strictly before today (exceeded timeline)
export const isExceededTimeline = (dateStr) => {
    const d = parseDateStr(dateStr);
    if (!d) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const due = new Date(d);
    due.setHours(0, 0, 0, 0);
    return due.getTime() < today.getTime();
};

// Returns true if dateStr is today or within the rest of this week (Mon–Sun)
export const isDueSoon = (dateStr) => {
    const d = parseDateStr(dateStr);
    if (!d) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const due = new Date(d);
    due.setHours(0, 0, 0, 0);
    if (due.getTime() < today.getTime()) return false;
    const endOfWeek = new Date(today);
    const dayOfWeek = today.getDay();
    const daysUntilSunday = dayOfWeek === 0 ? 0 : 7 - dayOfWeek;
    endOfWeek.setDate(today.getDate() + daysUntilSunday);
    endOfWeek.setHours(23, 59, 59, 999);
    return due.getTime() <= endOfWeek.getTime();
};

export const formatDisplayDate = (dateStr) => {
    const d = parseDateStr(dateStr);
    if (!d) return dateStr || 'N/A';
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
};

export const formatFullTimestamp = (dateStr) => {
    if (!dateStr) return 'N/A';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    let hours = d.getHours();
    const minutes = String(d.getMinutes()).padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12;
    const strHours = String(hours).padStart(2, '0');
    return `${day}/${month}/${year} ${strHours}:${minutes} ${ampm}`;
};

export const getStatusValue = (prop) => {
    return prop.proposal_stage || prop.global_status?.label || prop.status || prop.rfp_status || prop.extraction_data?.status || '';
};

// Re-exported from central statusConstants — single source of truth for status colors/classes
export const getStatusClass = _getStatusCssClass;
export const getStatusLabel = _getStatusLabelFn;

export const capitalize = (str) =>
    String(str).replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

export const ITEMS_PER_PAGE = 9;

// Helper to determine if a proposal is expired (by status code 10, status text, or exceeded timeline)
export const isProposalExpired = (rfp) => {
    if (!rfp) return false;
    const code = Number(rfp.global_status?.code ?? rfp.status_code ?? rfp.status ?? rfp.proposal_stage);
    if (code === 10 || code === 56) return true;
    const stageStr = String(rfp.proposal_stage || rfp.global_status?.label || rfp.status || '').toLowerCase();
    if (stageStr.includes('expire')) return true;
    const isFinal = stageStr.includes('submit') || stageStr.includes('accept') || stageStr.includes('reject');
    const deadline = rfp.proposal_deadline || rfp.extraction_data?.submission_deadline;
    if (!isFinal && deadline && isExceededTimeline(deadline)) return true;
    return false;
};

export const STATUS_LABELS = {
    all: 'All',
    analysis: 'In Analysis',
    review: 'In Review',
    submitted: 'Submitted',
    finalised: 'Finalised',
    accepted: 'Accepted',
    rejected: 'Rejected',
    due_this_week: 'At Risk',
    expired: 'Expired'
};

export const MAIN_STATUS_KEYS = ['all', 'analysis', 'review', 'submitted'];
export const MORE_STATUS_KEYS = ['finalised', 'accepted', 'rejected', 'due_this_week', 'expired'];
